import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useLocation,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { RoavaRecoveryUI } from "@/components/RoavaRecoveryUI";
import { markBootSuccess } from "@/lib/boot-recovery";
import { EmergencyOfflineFallback, useOfflineRedirect } from "@/lib/offline/use-offline-redirect";
import roavaPathLogoAsset from "@/assets/roava-path-logo.png.asset.json";
import roavaWebsiteLogoAsset from "@/assets/roava-website-logo.png.asset.json";
import { OnboardingProvider } from "@/components/onboarding/OnboardingProvider";
import { TourController } from "@/components/onboarding/TourController";
import { ConfirmEndTripSheet } from "@/components/live-activity/ConfirmEndTripSheet";

const faviconUrl = roavaPathLogoAsset.url;
const websiteLogoUrl = roavaWebsiteLogoAsset.url;
const SITE_URL = "https://roava.lovable.app";


function NotFoundComponent() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">{t("shell.notfound_title")}</h1>
        <h2 className="mt-4 text-xl font-semibold">{t("shell.notfound_heading")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("shell.notfound_body")}
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
          >
            {t("shell.go_home")}
          </Link>
        </div>
      </div>
    </div>
  );
}

// Minified React invariant codes we care about. React replaces invariant
// messages with `Minified React error #N` in production; this maps the codes
// we've actually hit so [ROAVA_ROUTE_ERROR] logs are readable in Xcode without
// shipping source maps.
const REACT_ERROR_NAMES: Record<string, string> = {
  "130": "Objects are not valid as a React child",
  "152": "Component suspended while responding to synchronous input",
  "185": "Maximum update depth exceeded",
  "300": "Rendered more hooks than during the previous render",
  "301": "Rendered fewer hooks than expected",
  "310": "Rendered more hooks than during the previous render",
  "321": "Invalid hook call",
  "418": "Hydration failed",
  "419": "Server rendering aborted",
  "421": "Suspense boundary received an update before it finished hydrating",
  "422": "Hydration mismatch",
  "423": "Hydration failed and the error was thrown during recovery",
  "425": "Text content does not match server-rendered HTML",
};
function decodeReactError(message: string): string | null {
  const m = message.match(/Minified React error #(\d+)/);
  if (!m) return null;
  const code = m[1];
  const name = REACT_ERROR_NAMES[code] ?? "unknown invariant";
  return `react#${code} "${name}"`;
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation();
  // Grep-able tag for Xcode console.
  const name = (error as any)?.name ?? "Error";
  const message = (error as any)?.message ?? String(error);
  const stack = ((error as any)?.stack ?? "").split("\n").slice(0, 6).join("\n");
  const decoded = decodeReactError(message);
  // Log once per real error (not once per re-render of the boundary).
  useEffect(() => {
    const prefix = decoded ? `[ROAVA_ROUTE_ERROR] ${decoded} — ` : `[ROAVA_ROUTE_ERROR] `;
    console.error(`${prefix}${name}: ${message}\n${stack}`);
  }, [name, message, stack, decoded]);
  const router = useRouter();
  const [isOffline, setIsOffline] = useState<boolean>(
    typeof navigator !== "undefined" && navigator.onLine === false,
  );
  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  const handleRetry = () => {
    try {
      router.invalidate();
      reset();
    } catch {
      /* swallow — RoavaRecoveryUI will escalate to a cache-busting reload */
    }
  };
  const goOffline = () => {
    console.warn("[error-boundary] offline -> offline-control");
    try {
      router.navigate({ to: "/offline-control", replace: true });
      reset();
    } catch { /* noop */ }
  };
  if (isOffline) {
    return (
      <div>
        <RoavaRecoveryUI
          onRetry={goOffline}
          title={t("shell.offline_title")}
          body={t("shell.offline_body")}
        />
      </div>
    );
  }
  return (
    <div>
      <RoavaRecoveryUI onRetry={handleRetry} />
      {/* Hidden technical details — kept in the DOM only for native log
          capture; not surfaced to normal users. */}
      <details
        style={{ position: "fixed", bottom: 8, left: 8, right: 8, fontSize: 11, color: "#777" }}
      >
        <summary style={{ cursor: "pointer", listStyle: "none" }}>·</summary>
        <div style={{ marginTop: 6 }}>
          <div>
            <strong>{name}:</strong> <span>{message}</span>
          </div>
          {stack && (
            <pre style={{ maxHeight: 200, overflow: "auto", whiteSpace: "pre-wrap", fontSize: 10 }}>
              {stack}
            </pre>
          )}
        </div>
      </details>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "UTF-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0, viewport-fit=cover" },
      { property: "og:site_name", content: "Roava by WGP Apps" },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "icon", type: "image/png", href: faviconUrl },
      { rel: "apple-touch-icon", href: faviconUrl },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              name: "Roava",
              url: SITE_URL,
            },
            {
              "@type": "Organization",
              name: "WGP Apps",
              url: "https://wgpapp.com",
              logo: `${SITE_URL}${websiteLogoUrl}`,
            },
          ],
        }),
      },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function AuthSync() {
  const router = useRouter();
  const qc = useQueryClient();
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      router.invalidate();
      qc.invalidateQueries();
      // If a new user signed up with email confirmation required, we stashed
      // their validated invite code. Redeem it now that there's a session.
      if (event === "SIGNED_IN") {
        void (async () => {
          const { getPendingCode, clearPendingCode } = await import("@/lib/pending-invite");
          const code = getPendingCode();
          if (!code) return;
          try {
            const { error } = await supabase.rpc("redeem_invite_code", { _code: code });
            if (!error) clearPendingCode();
          } catch { /* leave pending; will retry on next SIGNED_IN */ }
          qc.invalidateQueries();
        })();
      }
    });
    return () => subscription.unsubscribe();
  }, [router, qc]);
  return null;
}

// Scroll the window to the top on every forward in-app navigation
// (Link, useNavigate, redirect). Back/forward (POP) is left alone so the
// browser's native scroll restoration keeps the previous position.
function ScrollToTop() {
  const router = useRouter();
  const pathname = useLocation({ select: (l) => l.pathname });
  useEffect(() => {
    if ((router.history as any).action === "POP") return;
    const scroll = () => {
      window.scrollTo(0, 0);
      // The real scroll container in this app is #root (body has overflow:hidden).
      const root = document.getElementById("root");
      if (root) root.scrollTop = 0;
    };
    scroll();
    // Multiple ticks for content that paints after mount (suspense, images, lists).
    requestAnimationFrame(scroll);
    setTimeout(scroll, 60);
  }, [pathname, router]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const loc = useLocation();
  const { isOffline: rootOfflineRedirectActive } = useOfflineRedirect();
  const showEmergencyOfflineFallback =
    rootOfflineRedirectActive &&
    loc.pathname !== "/login" &&
    !loc.pathname.startsWith("/login/");
  useEffect(() => {
    // Boot succeeded — clear cold-launch recovery guard so the next launch
    // starts fresh, and record the current web build version.
    markBootSuccess();

    // Install the native UploadWorker → JS bridge listener once on mount.
    // No-op until an upload subsystem registers a drain handler.
    void import("@/lib/native/upload-drain").then((m) => m.installUploadDrainListener());

    // Init the Live Activity controller (clears orphan activities from a
    // prior cold-launch). Idempotent. No-op on non-iOS.
    void import("@/lib/live-activity/controller").then((m) =>
      m.liveActivityController.init().catch(() => { /* silent */ }),
    );

    // Wire Dynamic Island AppIntent command dispatcher. Drains anything queued
    // while the app was suspended and subscribes for live taps. iOS-only.
    void import("@/lib/live-activity/commands").then((m) =>
      m.initLiveActivityCommands().catch(() => { /* silent */ }),
    );

    // Silently re-register APNs token for users who already granted permission.
    // Does NOT trigger the iOS notification prompt; first-time opt-in lives in
    // /permissions (reminders) and /notifications (push) behind a user tap.
    void import("@/lib/native/push-register").then((m) =>
      m.ensurePushRegisteredIfAlreadyGranted().catch(() => {/* silent */}),
    );

    // Install local-notification watchers (tracking_stopped, queue_stuck_wifi,
    // trip_recording_48h). Web-safe — only LocationBG paths require native.
    void import("@/lib/native/notification-watchers").then((m) =>
      m.installNotificationWatchers(),
    );

    // Deep-link listener: handles roava:// custom-scheme URLs and Universal
    // Links from roava.wgpapp.com / roava.lovable.app. Idempotent + safe on web.
    let disposeDeepLinks: (() => void) | null = null;
    void import("@/lib/native/deeplinks").then(async (m) => {
      disposeDeepLinks = await m.installDeepLinkHandler(router);
    });

    // Foreground drain: when the app returns to active (e.g. after a flight
    // when the user opens the app on the runway), pull anything sitting in
    // the native LocationBG queue into the JS outbox, then push the outbox
    // to the server. Both drains are single-flight + cheap when empty.
    let disposeAppState: (() => void) | null = null;
    void (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const [{ drainNativeLocationQueue }, { drainPingOutbox }, { getCachedTripSync }] = await Promise.all([
          import("@/lib/native/location-bg"),
          import("@/lib/native/ping-outbox"),
          import("@/lib/offline/trip-cache"),
        ]);
        const h = await App.addListener("appStateChange", async ({ isActive }) => {
          if (!isActive) return;
          const tripId = getCachedTripSync()?.id ?? null;
          try { await drainNativeLocationQueue(tripId); } catch { /* ignore */ }
          try { await drainPingOutbox(); } catch { /* ignore */ }
        });
        disposeAppState = () => h.remove().catch(() => {});
      } catch { /* web — no-op */ }
    })();

    return () => {
      disposeDeepLinks?.();
      disposeAppState?.();
    };
  }, [router]);
  return (
    <QueryClientProvider client={queryClient}>
      <AuthSync />
      <ScrollToTop />
      <EmergencyOfflineFallback active={showEmergencyOfflineFallback} />
      <main><Outlet /></main>
      <OnboardingProvider />
      <TourController />
      <ConfirmEndTripSheet />
      <Toaster />
    </QueryClientProvider>
  );
}
